import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiError } from '@/api/errors'
import type { Priority } from '@/api/types'
import { priorityLabel } from '@/i18n/labels'
import { useRouteTemplatesList } from '@/features/refs/queries'
import { Button, Field, Input, Select, Sheet, Textarea } from '@/ui'
import { useCreateOrder } from './queries'

const PRIORITIES: Priority[] = ['LOW', 'NORMAL', 'HIGH', 'URGENT']

function toIso(local: string): string {
  // datetime-local has no timezone; interpret in the browser's local zone.
  return local ? new Date(local).toISOString() : ''
}

export function OrderCreateSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const create = useCreateOrder()
  const routes = useRouteTemplatesList()

  const [clientName, setClientName] = useState('')
  const [productName, setProductName] = useState('')
  const [dueAt, setDueAt] = useState('')
  const [quantity, setQuantity] = useState('1')
  const [priority, setPriority] = useState<Priority>('NORMAL')
  const [routeId, setRouteId] = useState('')
  const [clientPhone, setClientPhone] = useState('')
  const [description, setDescription] = useState('')

  const activeRoutes = (routes.data?.items ?? []).filter((r) => r.is_active)
  const errMessage = create.error instanceof ApiError ? create.error.message : null

  function submit(e: FormEvent) {
    e.preventDefault()
    create.mutate(
      {
        client_name: clientName.trim(),
        product_name: productName.trim(),
        due_at: toIso(dueAt),
        quantity: Number(quantity) || 1,
        priority,
        client_phone: clientPhone.trim() || undefined,
        description: description.trim() || undefined,
        route_template_id: routeId || undefined,
      },
      {
        onSuccess: (order) => {
          onClose()
          navigate(`/orders/${order.id}`)
        },
      },
    )
  }

  return (
    <Sheet open={open} onClose={onClose} title="Новый заказ">
      <form onSubmit={submit} className="max-h-[70vh] overflow-y-auto">
        <Field label="Клиент *">
          <Input value={clientName} onChange={(e) => setClientName(e.target.value)} required />
        </Field>
        <Field label="Изделие *">
          <Input value={productName} onChange={(e) => setProductName(e.target.value)} required />
        </Field>
        <Field label="Срок *">
          <Input
            type="datetime-local"
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
            required
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Количество">
            <Input
              type="number"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </Field>
          <Field label="Приоритет">
            <Select value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {priorityLabel[p]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="Маршрут">
          <Select value={routeId} onChange={(e) => setRouteId(e.target.value)}>
            <option value="">— выбрать позже —</option>
            {activeRoutes.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Телефон клиента">
          <Input value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} />
        </Field>
        <Field label="Описание">
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>

        {errMessage && (
          <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-risk-red">{errMessage}</p>
        )}
        <div className="flex gap-2">
          <Button type="button" variant="secondary" fullWidth onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" fullWidth loading={create.isPending}>
            Создать
          </Button>
        </div>
      </form>
    </Sheet>
  )
}
